import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ChartCard, HistogramChart } from "@/components/Elements/Charts";
import ListStatisticsCardExtra from "@/components/ListStatistics/ListStatisticsCardExtra";
import { useListStatistics } from "@/components/ListStatistics/ListStatisticsHooks";
import { PROJECTS_FILTER_KEYS } from "@/entities/projects";
import { useTranslate } from "@/modules/translate";

const STATS_DATA_KEY = "yearly";
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_RANGE = Array.from({ length: 5 }, (_, i) => `${CURRENT_YEAR + i}`);

const ProjectsCostPerYearHistogramChart = () => {
  const { t } = useTranslation();
  const { translateNumber, translateCost } = useTranslate();

  const { loading, fetching, statsData, filters, clearFilter, onFilterChange } =
    useListStatistics({
      statsDataKey: STATS_DATA_KEY,
      filterKey: PROJECTS_FILTER_KEYS.DUE_DATE,
    });

  const filteredYear = useMemo(() => {
    const dateRangeStart = filters?.[0];
    return dateRangeStart?.split("-")?.[0];
  }, [filters]);

  const chartData = useMemo(() => {
    return YEAR_RANGE.map((year) => {
      const data = statsData.find((entry) => entry.year === year);
      const value = data
        ? parseInt(data.total_cost ?? data.interventions_cost ?? 0)
        : 0;

      return { category: year, value };
    });
  }, [statsData]);

  const onSelectedCategoryChange = (year) => {
    const dateRange = year ? [`${year}-01-01`, `${year}-12-31`] : undefined;
    if (dateRange !== undefined) {
      onFilterChange({
        filterKey: PROJECTS_FILTER_KEYS.DUE_DATE,
        filterValues: dateRange,
      });
    } else {
      clearFilter(PROJECTS_FILTER_KEYS.DUE_DATE);
    }
  };

  return (
    <ChartCard
      title={t(`project:statistics.costPerYear.title`)}
      extra={
        <ListStatisticsCardExtra filterKey={PROJECTS_FILTER_KEYS.DUE_DATE} />
      }
    >
      <HistogramChart
        id="cost-per-year"
        loading={loading}
        fetching={fetching}
        data={chartData}
        controlled={{ selectedCategory: filteredYear }}
        formatValueLabel={(value) =>
          translateNumber(value, { notation: "compact" })
        }
        formatTooltipValue={translateCost}
        onSelectedCategoryChange={onSelectedCategoryChange}
      />
    </ChartCard>
  );
};

export default ProjectsCostPerYearHistogramChart;
